// La lista org-wide de observaciones de facility (#17) — desde ADR-0134
// (Fase 4) sobre el hilo de observación: estado, filtro y kanban por FASE
// del hilo (`entryThread.ts`), asignar con enlace al contratista
// (`AssignDrawer` → /findings/:id/assign) y el detalle del hilo en
// /observaciones/:id. La delegación (DelegationStatusControl, /delegate,
// /delegation-status) se retiró: el servidor responde 410.
//
// Las acciones de cada fila salen del grafo de la receta para el carril
// del usuario (`resolveGestionActions`): «Asignar» / «Reasignar» sólo
// aparece donde ese carril alcanza la asignación (supervisor y gerente;
// nunca el gerente de marca, que sólo lee). Anular, Conforme y No conforme
// viven en el detalle, con su motivo.

import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import type {
  FacilityDashboardAsset,
  FacilityDashboardEntry,
  FacilityDashboardProvider,
  ObservationPhaseVocabulary,
} from "@bitacorapro/api-types";
import { AssignDrawer } from "../components/AssignDrawer";
import { BrandStrip } from "../components/BrandStrip";
import { EvidenciaCell } from "../components/EvidenciaCell";
import {
  FacilityFiltros,
  FiltrosSinCoincidencias,
  type FacilityChip,
} from "../components/facility/FacilityFiltros";
import { Icon } from "../components/icons";
import { EstadoPills } from "../components/ObservacionPills";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { Pager } from "../components/Pager";
import { Skel } from "../components/Skel";
import { useFacilityDashboard, useRecipe } from "../lib/api/queries";
import { useSession } from "../lib/auth/useSession";
import { downloadCsv, todayIsoDate, toRfc4180 } from "../lib/csv";
import {
  clampPage,
  hasActiveFilters,
  normalizePagingParams,
  optionsFor,
  PAGE_SIZE,
  pageSlice,
  resultados,
  totalPages as countPages,
} from "../lib/facility/listState";
import {
  applyFilters,
  buildFilas,
  CLEAR_FILTERS,
  ESTADOS,
  FILTER_KEYS,
  listConfig,
  parseListState,
  PLAZOS,
  plazoLabel,
  severityDomain,
  SIN_PROVEEDOR,
  tiendaLabel,
  valueOf,
  writeListState,
  type FilterKey,
  type ObsPatch,
  type Plazo,
} from "../lib/facility/observacionesList";
import { useListAnnouncer } from "../lib/facility/useListAnnouncer";
import { useQueryView } from "../lib/loading/useQueryView";
import { hasContextualProjectNav } from "../lib/nav";
import { resolveGestionActions, type GestionActions } from "../lib/postventa/actions";
import {
  ESTADO_COLUMNS,
  columnOf,
  columnTitle,
  entryState,
  isEntryOpen,
  type EstadoColumnKey,
} from "../lib/postventa/entryThread";
import { threadWords } from "../lib/postventa/words";
import {
  daysSince,
  observationActorLane,
  observationHeadline,
  sevLabel,
  sevPillClass,
  storeTitle,
} from "../lib/util";
import { ObservacionesBodySkeleton } from "./Observaciones.skeleton";

type View = "table" | "kanban";

export function Observaciones() {
  // The recipe gates the dashboard read: until it is known the fetch
  // stays disabled (a postventa org 403s on /facility-dashboard —
  // ADR-0072 Phase B), and ObservacionesRoute redirects a project-framed
  // vertical before this ever enables. `waitingOn` keeps the one
  // skeleton on screen across both reads, so the route mounts this page
  // straight away instead of a blank while the recipe resolves.
  const recipeQuery = useRecipe();
  const recipe = recipeQuery.data;
  const dashboardQuery = useFacilityDashboard(
    recipe !== undefined && !hasContextualProjectNav(recipe)
  );
  const data = dashboardQuery.data;
  const recipeView = useQueryView(recipeQuery);
  const dashboardView = useQueryView(dashboardQuery, { waitingOn: [recipeQuery] });
  const vocab: ObservationPhaseVocabulary | undefined = recipe;
  const words = threadWords(recipe);
  const [view, setView] = useState<View>("table");
  const [assigning, setAssigning] = useState<FacilityDashboardEntry | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Parent-owned, not rendered inside AssignDrawer — the drawer is gone
  // by the time a live-region announcement inside it would be perceived
  // (WCAG 4.1.3; accessibility-expert, Arrow pilot review 2026-08-10).
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  // Rows whose status was just changed from this screen. They stay
  // visible even once they no longer match the active Estado / Plazo
  // filter — a row disappearing from under the cursor immediately after
  // you act on it reads as "did that work?", and it also shifts
  // pagination mid-interaction. Cleared when the user changes a filter or
  // the view, so the exemption lasts exactly until they change what they
  // are looking at. Deliberately not a timer: a timed eviction can yank a
  // row out while a keyboard or screen-reader user is still inside it.
  const [recentlyChangedIds, setRecentlyChangedIds] = useState<ReadonlySet<string>>(new Set());
  const markRecentlyChanged = (id: string) =>
    setRecentlyChangedIds((prev) => new Set(prev).add(id));
  const [live, announce] = useListAnnouncer();

  const session = useSession();
  // Server is authoritative — the route re-derives asset scope and the
  // lane per request. This only avoids rendering a control the caller
  // could never complete a write with (brand_manager declares no lane).
  const lane = observationActorLane(recipe, session?.organization.role);
  const actionsOf = (o: FacilityDashboardEntry): GestionActions =>
    resolveGestionActions(recipe, o.observationStatus ?? null, lane);
  // The assign drawer tells the supervisor the jefe de tienda will be
  // told — only when the recipe's assigned status says so.
  const assignNotifies = (o: FacilityDashboardEntry) =>
    actionsOf(o).assignTarget?.notifiesAssetParty === true;

  // Filters, search and page live in the URL (lib/facility/observacionesList.ts):
  // `?tienda=`, `?severidad=`, `?estado=`, `?proveedor=`, `?plazo=`,
  // `?marca=`, `?q=`, `?pagina=`. DetalleTienda's «Ver cola completa» still
  // deep-links with `?asset=`, read as `tienda` and rewritten on the spot.
  const [searchParams, setSearchParams] = useSearchParams();
  const severities = useMemo(() => severityDomain(recipe), [recipe]);
  const state = useMemo(() => parseListState(searchParams, severities), [searchParams, severities]);

  const assetById = useMemo(() => {
    const m = new Map<string, FacilityDashboardAsset>();
    for (const a of data?.byAsset ?? []) m.set(a.id, a);
    return m;
  }, [data]);

  const providerById = useMemo(() => {
    const m = new Map<string, FacilityDashboardProvider>();
    for (const p of data?.byProvider ?? []) m.set(p.id, p);
    return m;
  }, [data]);

  // «Hoy» is fixed per dashboard load: the plazo is recomputed on
  // refresh, not on every render.
  const now = useMemo(() => Date.now(), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const filas = useMemo(
    () => buildFilas(data?.entries ?? [], assetById, vocab, now),
    [data, assetById, vocab, now]
  );
  const filtered = useMemo(
    () => applyFilters(filas, state, { keep: recentlyChangedIds }).map((f) => f.e),
    [filas, state, recentlyChangedIds]
  );

  function update(patch: ObsPatch) {
    setSearchParams((prev) => writeListState(prev, patch), { replace: true });
  }
  /** Changes filters (back to page 1), ends the just-changed exemption and
   *  announces the result, computed on the next state rather than this
   *  render's. */
  function patchAndAnnounce(patch: ObsPatch) {
    const next = parseListState(writeListState(searchParams, patch), severities);
    update(patch);
    setRecentlyChangedIds(new Set());
    announce(resultados(applyFilters(filas, next).length));
  }

  const brandOptions = useMemo(() => {
    if (!data) return [];
    const counts = new Map<
      string,
      { id: string; name: string; count: number; logoUrl: string | null }
    >();
    for (const o of data.entries) {
      if (!isEntryOpen(o, vocab)) continue;
      const asset = assetById.get(o.assetId);
      if (!asset?.brandId || !asset.brandName) continue;
      const entry = counts.get(asset.brandId);
      if (entry) entry.count += 1;
      else
        counts.set(asset.brandId, {
          id: asset.brandId,
          name: asset.brandName,
          count: 1,
          logoUrl: asset.brandLogoUrl,
        });
    }
    return Array.from(counts.values()).sort((a, b) => a.name.localeCompare(b.name, "es"));
  }, [data, assetById, vocab]);

  // A failed recipe read ends in its own error: the dashboard read it
  // gates never enables, so its view alone would settle on `disabled`.
  const failed =
    recipeView.kind === "error"
      ? recipeView
      : dashboardView.kind === "error"
        ? dashboardView
        : null;
  const ready = failed === null && dashboardView.kind === "ready" ? dashboardView : null;
  const openCount = ready ? ready.data.entries.filter((e) => isEntryOpen(e, vocab)).length : 0;

  const pages = countPages(filtered.length);
  const pagina = clampPage(state.pagina, filtered.length);

  // A URL that is not the canonical one for what is painted (page out of
  // range, `?pagina=abc`, a legacy `?obs_page=` / `?asset=`) is corrected
  // in place.
  const isReady = ready !== null;
  useEffect(() => {
    if (!isReady) return;
    const canon = normalizePagingParams(searchParams, pagina, listConfig(severities));
    if (canon) setSearchParams(canon, { replace: true });
  }, [isReady, pagina, searchParams, setSearchParams, severities]);

  // The real header always renders; only what depends on data waits, as
  // a Skel inside its own line box.
  const header = (
    <div className="page-header">
      <div className="page-header__lead">
        <div className="page-header__eyebrow">
          {ready ? (
            `Seguimiento · ${openCount} sin cerrar`
          ) : failed ? (
            "Seguimiento"
          ) : (
            <Skel variant="text" width={150} />
          )}
        </div>
        <h1 className="page-header__title">Observaciones</h1>
        <p className="page-header__sub">
          {recipe ? (
            <>
              Observaciones detectadas en los reportes: asígnalas a un {words.assigneeLower} con un
              enlace y revisa lo que te devuelven resuelto.
            </>
          ) : (
            <>
              <Skel variant="text" width="90%" />
              <Skel variant="text" width="45%" />
            </>
          )}
        </p>
      </div>
      <div className="page-header__actions">
        <ViewToggle
          value={view}
          onChange={(v) => {
            setView(v);
            setRecentlyChangedIds(new Set());
          }}
        />
      </div>
    </div>
  );

  if (failed) {
    return (
      <div className="page">
        {header}
        {failed.offline ? (
          <PageError
            title="Sin conexión"
            description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
            onRetry={failed.retry}
          />
        ) : (
          <PageError
            title="No pudimos cargar las observaciones"
            description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
            onRetry={failed.retry}
          />
        )}
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="page">
        {header}
        {dashboardView.kind === "loading" && (
          <>
            <PageSkeleton label="Cargando las observaciones…" visible={dashboardView.visible}>
              <ObservacionesBodySkeleton view={view} vocab={vocab} />
            </PageSkeleton>
            {dashboardView.slow && (
              <div className="notice-banner notice-banner--action">
                <span>
                  <strong>Esto está tardando más de lo normal.</strong> Revisa tu conexión. Seguimos
                  intentando.
                </span>
                <button
                  type="button"
                  className="btn btn--secondary btn--sm"
                  onClick={() =>
                    // A disabled query still fetches on refetch(): while
                    // the recipe is what is slow, retry the recipe.
                    void (recipe === undefined ? recipeQuery : dashboardQuery).refetch()
                  }
                >
                  Reintentar
                </button>
              </div>
            )}
          </>
        )}
        {/* `disabled`: a project-framed recipe, which ObservacionesRoute
            redirects — nothing is on its way, so no skeleton either. */}
      </div>
    );
  }

  const paginated = pageSlice(filtered, pagina);
  const assigningAsset = assigning ? assetById.get(assigning.assetId) : undefined;
  const filtrosActivos = hasActiveFilters(state, FILTER_KEYS);

  const chip = (
    key: FilterKey,
    label: string,
    labelOf: (v: string) => string,
    domain?: readonly string[],
    pinFirst?: readonly string[]
  ): FacilityChip => ({
    key,
    label,
    options: optionsFor(filas, state, key, FILTER_KEYS, valueOf, {
      labelOf,
      ...(domain ? { domain } : {}),
      ...(pinFirst ? { pinFirst } : {}),
    }),
    value: state[key],
  });
  const chips: FacilityChip[] = [
    chip("tienda", "Tienda", (v) => tiendaLabel(assetById.get(v))),
    chip("severidad", "Criticidad", (v) => sevLabel(v), severities),
    chip("estado", "Estado", (v) => columnTitle(v as EstadoColumnKey, vocab), ESTADOS),
    chip(
      "proveedor",
      words.assignee,
      (v) =>
        v === SIN_PROVEEDOR
          ? "Sin asignar"
          : (providerById.get(v)?.shortName ?? `${words.assignee} seleccionado`),
      undefined,
      [SIN_PROVEEDOR]
    ),
    chip("plazo", "Plazo", (v) => plazoLabel(v as Plazo, words.assigneeLower), PLAZOS),
  ];
  const clearAll = () => patchAndAnnounce(CLEAR_FILTERS);
  const sinCoincidencias =
    filtered.length === 0 && filtrosActivos ? (
      <FiltrosSinCoincidencias noun="observación" onClear={clearAll} />
    ) : null;

  return (
    <div className="page">
      {header}

      {ready.refreshFailed && (
        <div className="notice-banner notice-banner--action">
          <span>No pudimos actualizar. Mostrando lo último que tienes guardado.</span>
          <button
            type="button"
            className="btn btn--secondary btn--sm"
            onClick={() => void dashboardQuery.refetch()}
          >
            Reintentar
          </button>
        </div>
      )}
      {ready.revalidatingHint && <p className="refresh-hint">Actualizando…</p>}

      <BrandStrip
        brands={brandOptions}
        value={state.marca[0] ?? "all"}
        onChange={(id) => patchAndAnnounce({ marca: id === "all" ? [] : [id] })}
        countLabel="abiertas"
      />

      <div className="panel">
        <FacilityFiltros
          searchPlaceholder="Buscar observación, tienda o recinto…"
          q={state.q}
          onSearch={(q) => patchAndAnnounce({ q })}
          chips={chips}
          onChip={(key, next) => patchAndAnnounce({ [key as FilterKey]: next })}
          active={filtrosActivos}
          onClear={clearAll}
          actions={
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => exportObservacionesCsv(filtered, assetById, providerById, vocab)}
              disabled={filtered.length === 0}
              aria-label="Exportar observaciones a CSV"
            >
              <Icon.Download s={13} /> Exportar CSV
            </button>
          }
          resultCount={filtered.length}
          live={live}
        />

        {view === "table" ? (
          <>
            <ObsTable
              rows={paginated}
              assetById={assetById}
              providerById={providerById}
              onAssign={setAssigning}
              canAssign={(o) => actionsOf(o).assignReachable}
              vocab={vocab}
              empty={sinCoincidencias}
            />
            {filtered.length > 0 && (
              <Pager
                currentPage={pagina}
                totalPages={pages}
                totalItems={filtered.length}
                pageSize={PAGE_SIZE}
                onPage={(n) => {
                  update({ pagina: n });
                  announce(`Página ${n} de ${pages}`);
                }}
              />
            )}
          </>
        ) : sinCoincidencias ? (
          <div className="panel__body--flush">{sinCoincidencias}</div>
        ) : (
          <ObsKanban
            rows={filtered}
            assetById={assetById}
            providerById={providerById}
            vocab={vocab}
            providerNoun={words.assigneeLower}
          />
        )}
      </div>

      {assigning && (
        <AssignDrawer
          obs={assigning}
          storeName={assigningAsset ? storeTitle(assigningAsset) : "—"}
          notifiesAssetParty={assignNotifies(assigning)}
          onClose={() => {
            // The row keeps its place under the active filter until the
            // user changes it — see recentlyChangedIds.
            markRecentlyChanged(assigning.id);
            setAssigning(null);
          }}
          onSent={(msg) => setToast(msg)}
        />
      )}

      {toast && (
        <div className="toast-zone">
          <div className="toast toast--ok" role="status" aria-live="polite">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}

// ── CSV export ────────────────────────────────────────────────────

function exportObservacionesCsv(
  rows: FacilityDashboardEntry[],
  assetById: Map<string, FacilityDashboardAsset>,
  providerById: Map<string, FacilityDashboardProvider>,
  vocab: ObservationPhaseVocabulary | undefined
) {
  const header = [
    "id",
    "fecha",
    "tienda",
    "area",
    "severidad",
    "estado",
    "asignado_a",
    "titulo",
    "descripcion",
  ];
  const data = rows.map((o) => {
    const asset = assetById.get(o.assetId);
    const provider = o.providerId ? providerById.get(o.providerId) : null;
    return [
      o.id,
      o.findingObservedAt ?? o.findingCreatedAt ?? "",
      asset?.unit ?? asset?.address ?? "",
      o.areaName ?? "",
      sevLabel(o.severity),
      entryState(o, vocab).label,
      provider ? provider.shortName : "Sin asignar",
      // Same headline the table shows, so a spreadsheet sorts and filters
      // on what the screen calls the observation; the full description
      // stays its own column.
      observationHeadline(o).text,
      o.humanDescription ?? o.aiDescription ?? o.element ?? "",
    ];
  });
  const csv = toRfc4180([header, ...data]);
  downloadCsv(csv, `observaciones-${todayIsoDate()}.csv`);
}

// ── Sub-components ────────────────────────────────────────────────

function ViewToggle({ value, onChange }: { value: View; onChange: (v: View) => void }) {
  return (
    <div className="seg-toggle" role="group" aria-label="Cambiar vista">
      <button
        type="button"
        className={`seg-toggle__btn ${value === "table" ? "seg-toggle__btn--active" : ""}`}
        onClick={() => onChange("table")}
      >
        Tabla
      </button>
      <button
        type="button"
        className={`seg-toggle__btn ${value === "kanban" ? "seg-toggle__btn--active" : ""}`}
        onClick={() => onChange("kanban")}
      >
        Kanban
      </button>
    </div>
  );
}

function ObsTable({
  rows,
  assetById,
  providerById,
  onAssign,
  canAssign,
  vocab,
  empty,
}: {
  rows: FacilityDashboardEntry[];
  assetById: Map<string, FacilityDashboardAsset>;
  providerById: Map<string, FacilityDashboardProvider>;
  onAssign: (o: FacilityDashboardEntry) => void;
  /** The caller's lane reaches the assignment from this row's status. */
  canAssign: (o: FacilityDashboardEntry) => boolean;
  vocab: ObservationPhaseVocabulary | undefined;
  /** What to show instead of the generic empty state (filters left no row). */
  empty: ReactNode;
}) {
  const navigate = useNavigate();

  if (rows.length === 0) {
    if (empty) return <div className="panel__body--flush">{empty}</div>;
    return (
      <div className="panel__body--flush">
        <div className="empty">
          <div className="empty__title">Sin resultados</div>
          <div className="empty__sub">Ajusta los filtros para ver más observaciones.</div>
        </div>
      </div>
    );
  }
  return (
    <div className="panel__body--flush dtable-scroll">
      <table className="dtable dtable--fit">
        <thead>
          <tr>
            <th>Observación</th>
            <th>Tienda</th>
            <th>Severidad</th>
            <th>Evidencia</th>
            <th>Estado</th>
            <th>Asignada a</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((o) => {
            const asset = assetById.get(o.assetId);
            const provider = o.providerId ? (providerById.get(o.providerId) ?? null) : null;
            const age = daysSince(o.findingObservedAt ?? o.findingCreatedAt);
            const headline = observationHeadline(o);
            // Matches Tiendas/Proyectos/DetalleProyecto's row-link
            // pattern: real interactive children (Link, buttons, the
            // Estado combobox) handle their own activation; the row's
            // onClick is a mouse-affordance enhancement, keyboard users
            // still tab to the cell-level Link/buttons.
            function handleRowClick(e: React.MouseEvent<HTMLTableRowElement>) {
              if (
                (e.target as HTMLElement).closest(
                  'a, button, [role="button"], [role="combobox"], [role="listbox"], [role="option"], [role="dialog"]'
                )
              )
                return;
              navigate(`/observaciones/${o.id}`);
            }
            return (
              <tr key={o.id} className="row-link" onClick={handleRowClick}>
                <td className="col-fill">
                  <div className="cell-with-sub cell-with-sub--headline">
                    {/* A real Link, not just the row onClick: the row's
                        mouse handler is not keyboard-reachable (SC 2.1.1),
                        and the visible headline is the summary while the
                        full description rides along for AT only, so a
                        screen-reader user hears the whole text where a
                        sighted one reads the first sentence. */}
                    <Link to={`/observaciones/${o.id}`} className="cell-strong cell-strong--link">
                      {headline.text}
                      {headline.fullText && headline.fullText !== headline.text && (
                        <span className="sr-only"> — {headline.fullText}</span>
                      )}
                    </Link>
                    <span className="cell-sub">
                      {o.areaName}
                      {age !== null ? ` · hace ${age}d` : ""}
                    </span>
                  </div>
                </td>
                <td>
                  {asset ? (
                    <Link to={`/tiendas/${asset.id}`} className="cell-strong cell-strong--link">
                      {storeTitle(asset)}
                    </Link>
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  <span className={`st-pill ${sevPillClass(o.severity)}`}>
                    {sevLabel(o.severity)}
                  </span>
                </td>
                <td>
                  <EvidenciaCell count={o.evidenceCount} thumbUrls={o.evidenceThumbUrls} />
                </td>
                <td>
                  <EstadoPills state={entryState(o, vocab)} />
                </td>
                <td>
                  {provider ? (
                    <span>{provider.shortName}</span>
                  ) : (
                    <em className="cell-sub cell-sub--italic">Sin asignar</em>
                  )}
                </td>
                <td>
                  {/* Hidden, not disabled, where this lane has no
                      assignment from the row's status (closed, resolved
                      awaiting review, or a read-only role). */}
                  {canAssign(o) && (
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => onAssign(o)}
                    >
                      {o.providerId ? "Reasignar" : "Asignar"}
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// One column per thread phase (`ESTADO_COLUMNS`): every state lands in
// exactly one, so an anulada observación never vanishes from the board.
// Titles are the recipe's own state labels. A card opens the thread
// detail — assigning, anulando and the verdict live there, with their
// reasons; the table keeps the quick «Asignar».
function ObsKanban({
  rows,
  assetById,
  providerById,
  vocab,
  providerNoun,
}: {
  rows: FacilityDashboardEntry[];
  assetById: Map<string, FacilityDashboardAsset>;
  providerById: Map<string, FacilityDashboardProvider>;
  vocab: ObservationPhaseVocabulary | undefined;
  /** «contratista» — the empty-assignee line on a card. */
  providerNoun: string;
}) {
  const navigate = useNavigate();
  const byColumn = new Map<EstadoColumnKey, FacilityDashboardEntry[]>();
  for (const o of rows) {
    const key = columnOf(entryState(o, vocab));
    byColumn.set(key, [...(byColumn.get(key) ?? []), o]);
  }
  return (
    <div className="kanban">
      {ESTADO_COLUMNS.map((col) => {
        const items = byColumn.get(col.key) ?? [];
        return (
          <div key={col.key} className="kanban__col">
            <div className="kanban__head">
              <span className="kanban__head-title">{columnTitle(col.key, vocab)}</span>
              <span className="kanban__head-count">{items.length}</span>
            </div>
            <div className="kanban__body">
              {items.length === 0 ? (
                <div className="kanban__empty">vacío</div>
              ) : (
                items.map((o) => {
                  const asset = assetById.get(o.assetId);
                  const provider = o.providerId ? (providerById.get(o.providerId) ?? null) : null;
                  const headline = observationHeadline(o);
                  const state = entryState(o, vocab);
                  return (
                    // A native <button>, not a div with role="button": it
                    // gets Enter/Space activation, focus and the tab stop
                    // for free, and the accessible name says what the
                    // click does (open the thread) rather than reading
                    // the whole card. Children are spans — a button's
                    // content model is phrasing content only.
                    <button
                      key={o.id}
                      type="button"
                      className="obs-card"
                      onClick={() => navigate(`/observaciones/${o.id}`)}
                      aria-label={`Ver observación: ${headline.text}${
                        asset ? `, ${storeTitle(asset)}` : ""
                      }`}
                    >
                      <span className="obs-card__head">
                        <span className={`st-pill ${sevPillClass(o.severity)}`}>
                          {sevLabel(o.severity)}
                        </span>
                        <span className="obs-card__id">{asset ? storeTitle(asset) : "—"}</span>
                      </span>
                      <span className="obs-card__title">{headline.text}</span>
                      <span className="obs-card__meta">
                        {o.areaName}
                        {state.reopenCount > 0 ? ` · ciclo ${state.ciclo}` : ""}
                      </span>
                      <span className="obs-card__foot">
                        {provider ? (
                          <span className="cell-sub">{provider.shortName}</span>
                        ) : (
                          <em className="cell-sub cell-sub--italic">Sin {providerNoun}</em>
                        )}
                        <span className="obs-card__foot-spacer" />
                        <EvidenciaCell count={o.evidenceCount} thumbUrls={o.evidenceThumbUrls} />
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
